/**
 * Spec 006 — finance (open books, §19) fetch wrappers. Same discriminated-union contract as
 * lib/meetings.ts: these NEVER throw; each returns an explicit outcome the UI renders across the four
 * states. Money is integer öre. All requests send cookies; mutations echo the double-submit CSRF token.
 * A `conflict` result carries the server's specific message (closed-year / already-voided) verbatim.
 */
import { readCsrfToken } from './auth';

const TIMEOUT_MS = 10_000;

export type Result<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'invalid'; errors: Record<string, string>; message?: string }
  | { kind: 'conflict'; message: string }
  | { kind: 'forbidden'; message: string }
  | { kind: 'notfound' }
  | { kind: 'error' };

export type Direction = 'income' | 'expense';
export type YearStatus = 'open' | 'closed';

export interface FinancialYear {
  id: string;
  year: number;
  startsAt: string;
  endsAt: string;
  status: YearStatus;
}

export interface LedgerEntry {
  id: string;
  amountOre: number;
  direction: Direction;
  entryDate: string;
  category: string;
  description: string;
  isReimbursement: boolean;
  reimbursementSubjectId: string | null;
  voided: boolean;
  voidReason: string | null;
  createdAt: string;
}

export interface CategoryTotal {
  category: string;
  incomeOre: number;
  expenseOre: number;
}

export interface BudgetVsActual {
  direction: Direction;
  category: string;
  plannedOre: number;
  actualOre: number;
  remainingOre: number;
  overspent: boolean;
}

export interface LedgerView {
  year: number;
  status: YearStatus;
  startsAt: string;
  endsAt: string;
  balanceOre: number;
  incomeTotalOre: number;
  expenseTotalOre: number;
  entries: LedgerEntry[];
  categoryTotals: CategoryTotal[];
  budgetVsActual: BudgetVsActual[];
}

async function req(method: string, path: string, body?: unknown): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    const token = readCsrfToken();
    if (token) headers['x-csrf-token'] = token;
  }
  try {
    return await fetch(path, {
      method,
      credentials: 'include',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function toResult<T>(res: Response): Promise<Result<T>> {
  if (res.ok) return { kind: 'ok', data: (await res.json().catch(() => ({}))) as T };
  const body = (await res.json().catch(() => ({}))) as {
    message?: string | string[];
    errors?: Record<string, string>;
  };
  const firstMessage = Array.isArray(body.message) ? body.message[0] : body.message;
  if (res.status === 400) {
    // Nest's ValidationPipe returns message[] on validation failure; surface the first as the field error.
    return { kind: 'invalid', errors: body.errors ?? {}, message: firstMessage };
  }
  if (res.status === 403) return { kind: 'forbidden', message: firstMessage ?? 'You don’t have permission to do that.' };
  if (res.status === 404) return { kind: 'notfound' };
  if (res.status === 409) return { kind: 'conflict', message: firstMessage ?? 'That action isn’t possible right now.' };
  return { kind: 'error' };
}

const call = async <T>(method: string, path: string, body?: unknown): Promise<Result<T>> => {
  try {
    return await toResult<T>(await req(method, path, body));
  } catch {
    return { kind: 'error' };
  }
};

// ---- financial years ----
export const listYears = () => call<FinancialYear[]>('GET', '/api/finance/years');
export const openYear = (year: number) => call<FinancialYear>('POST', '/api/finance/years', { year });
export const closeYear = (id: string) => call<FinancialYear>('POST', `/api/finance/years/${id}/close`);
export const reopenYear = (id: string) => call<FinancialYear>('POST', `/api/finance/years/${id}/reopen`);

// ---- ledger view ----
export const getLedger = (year: number) => call<LedgerView>('GET', `/api/finance/years/${year}/ledger`);

// ---- entries ----
export interface EntryInput {
  year: number;
  amountOre: number;
  direction: Direction;
  entryDate: string;
  category: string;
  description: string;
  isReimbursement?: boolean;
  reimbursementSubjectId?: string;
}
export const createEntry = (b: EntryInput) => call<LedgerEntry>('POST', '/api/finance/entries', b);
export const editEntry = (id: string, b: Partial<Omit<EntryInput, 'year'>>) =>
  call<LedgerEntry>('PATCH', `/api/finance/entries/${id}`, b);
export const voidEntry = (id: string, reason: string) =>
  call<LedgerEntry>('POST', `/api/finance/entries/${id}/void`, { reason });

// ---- budget ----
export const setBudget = (b: { year: number; direction: Direction; category: string; plannedOre: number }) =>
  call<BudgetVsActual>('POST', '/api/finance/budget', b);

// ---- öre formatting (presentation only; never used in arithmetic) ----
export function formatOre(ore: number): string {
  const negative = ore < 0;
  const abs = Math.abs(ore);
  const kronor = Math.floor(abs / 100);
  const rest = abs % 100;
  const grouped = kronor.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${negative ? '-' : ''}${grouped},${rest.toString().padStart(2, '0')} €`;
}

/** Parse a "123,45" or "123.45" EUR string to integer minor units (cents); null if not a clean amount. */
export function parseSekToOre(input: string): number | null {
  const trimmed = input.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const [kr, dec = ''] = trimmed.split('.');
  const ore = Number(kr) * 100 + Number(dec.padEnd(2, '0'));
  return Number.isSafeInteger(ore) ? ore : null;
}
